import React, { useRef, useState, useEffect } from "react";
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaExpand } from "react-icons/fa";

const VideoPlayer = ({ src, title }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const update = () => setProgress(video.currentTime);
    video.addEventListener("timeupdate", update);
    return () => video.removeEventListener("timeupdate", update);
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) video.pause();
    else video.play();
    setIsPlaying(!isPlaying);
  };

  const handleLoaded = () => setDuration(videoRef.current.duration || 0);

  const handleSeek = (e) => {
    const time = e.target.value;
    videoRef.current.currentTime = time;
    setProgress(time);
  };

  const toggleMute = () => {
    const newMute = !isMuted;
    videoRef.current.muted = newMute;
    setIsMuted(newMute);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    videoRef.current.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleFullScreen = () => {
    const video = videoRef.current;
    if (video.requestFullscreen) video.requestFullscreen();
  };

  // Format time for display
  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-black/60 backdrop-blur-md border border-white/10 rounded-2xl shadow-lg overflow-hidden flex flex-col">
      <video
        ref={videoRef}
        src={src}
        onLoadedMetadata={handleLoaded}
        className="w-full max-h-[70vh] object-contain bg-black"
        onEnded={() => setIsPlaying(false)}
      />

      {/* Controls */}
      <div className="p-4 flex flex-col gap-3">
        {/* Progress bar */}
        <input
          type="range"
          min={0}
          max={duration}
          step="0.1"
          value={progress}
          onChange={handleSeek}
          className="w-full accent-indigo-500 cursor-pointer"
        />

        {/* Buttons row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-indigo-600 to-purple-700 hover:scale-110 transition-transform shadow-md"
            >
              {isPlaying ? (
                <FaPause className="text-white text-lg" />
              ) : (
                <FaPlay className="text-white text-lg pl-0.5" />
              )}
            </button>

            <button
              onClick={toggleMute}
              className="text-white text-xl hover:text-indigo-400 transition"
            >
              {isMuted || volume === 0 ? <FaVolumeMute /> : <FaVolumeUp />}
            </button>

            <input
              type="range"
              min={0}
              max={1}
              step="0.05"
              value={volume}
              onChange={handleVolumeChange}
              className="w-24 accent-indigo-500 cursor-pointer"
            />

            <span className="text-xs text-gray-300 ml-2">
              {formatTime(progress)} / {formatTime(duration)}
            </span>
          </div>

          <button
            onClick={handleFullScreen}
            className="text-white text-xl hover:text-indigo-400 transition"
          >
            <FaExpand />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
